import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type PropsWithChildren,
} from 'react';
import { createPortal } from 'react-dom';

const ZLayerGroup = ({
  id,
  registerZLayer: registerZLayer,
}: {
  id: number;
  registerZLayer: (id: number, node: SVGGElement) => void; // The function to register a layer container.
}) => {
  const nodeRef = useRef(null);

  useEffect(() => {
    // When this <g> mounts, register it
    if (nodeRef.current) {
      registerZLayer(id, nodeRef.current);
    }
  }, [id, registerZLayer]);

  return <g className={`zIndexLayer-${id}`} ref={nodeRef} />;
};

interface ZLayersContextProps {
  registerLayer: (id: number, node: SVGGElement) => void;
  layerNodes: Record<number, SVGGElement>;
}

export const ZLayersContext = createContext<ZLayersContextProps | undefined>(
  undefined,
);

export enum EZLayers {
  Background0,
  Background,
  Background1,
  Default0,
  Default,
  Default1,
  Foreground0,
  Foreground,
  Foreground1,
  Highlighted0,
  Highlighted,
  Highlighted1,
}

const zLayers = [
  EZLayers.Background0,
  EZLayers.Background,
  EZLayers.Background1,
  EZLayers.Default0,
  EZLayers.Default,
  EZLayers.Default1,
  EZLayers.Foreground0,
  EZLayers.Foreground,
  EZLayers.Foreground1,
  EZLayers.Highlighted0,
  EZLayers.Highlighted,
  EZLayers.Highlighted1,
];

export const useZLayers = (): ZLayersContextProps => {
  const context = useContext(ZLayersContext);
  if (!context) {
    throw new Error('useMapOverlay must be used within a MapOverlayProvider');
  }
  return context;
};

export const ZLayersProvider = ({ children }: PropsWithChildren) => {
  const [layerNodes, setLayerNodes] = useState<Record<number, SVGGElement>>({});

  // A helper to register a layer container
  const registerLayer = useCallback((id: number, node: SVGGElement) => {
    setLayerNodes(prev => ({ ...prev, [id]: node }));
  }, []);
  return (
    <ZLayersContext.Provider value={{ layerNodes, registerLayer }}>
      {children}
      {zLayers.map(id => (
        <ZLayerGroup key={id} id={+id} registerZLayer={registerLayer} />
      ))}
    </ZLayersContext.Provider>
  );
};

export const ZLayer = ({
  zIndex = 1,
  children,
}: PropsWithChildren<{ zIndex: EZLayers }>) => {
  const { layerNodes } = useZLayers();
  const container = layerNodes[zIndex];
  return container ? createPortal(children, container) : null;
};
